import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { CloudUpload, Database, Loader2, CheckCircle2, AlertCircle, RefreshCw, StopCircle, PanelRightOpen } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { StickyPageHeader, PageBody } from "@/components/dashboard/DashboardShell";
import { startBackupRun, processBackupStepFn, listBackupRuns, purgeOldSnapshotsFn } from "@/lib/backup-to-s3.functions";
import { getBackupReport, type BackupReport } from "@/lib/backup-report.functions";

export const Route = createFileRoute("/dashboard/admin/backups")({
  component: AdminBackupsPage,
});

type RunRow = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: "running" | "success" | "failed";
  triggered_by: "manual" | "cron";
  s3_bucket: string | null;
  s3_prefix: string | null;
  total_tables: number;
  total_rows: number;
  total_bytes: number;
  files_copied: number;
  files_reused: number;
  storage_bytes: number;
  auth_users_count: number;
  duration_ms: number | null;
  error_message: string | null;
  current_phase: string | null;
};

function AdminBackupsPage() {
  const { user, loading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const startRun = useServerFn(startBackupRun);
  const stepRun = useServerFn(processBackupStepFn);
  const fetchRuns = useServerFn(listBackupRuns);
  const fetchReport = useServerFn(getBackupReport);

  const [runs, setRuns] = useState<RunRow[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [report, setReport] = useState<BackupReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [phase, setPhase] = useState<string>("idle");
  const [message, setMessage] = useState<string>("");
  const [totals, setTotals] = useState<{ tablesDone: number; tablesTotal: number; tableRows: number; storageFilesCopied: number; storageFilesReused: number; storageQueueRemaining: number; authRows: number; staleDeleted: number; staleRemaining: number } | null>(null);
  const stopRef = useRef(false);

  const reload = async () => {
    setLoadingRuns(true);
    try {
      const res = await fetchRuns();
      setRuns((res.runs ?? []) as RunRow[]);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load backup runs");
    } finally {
      setLoadingRuns(false);
    }
  };

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const r = await fetchReport();
      setReport(r);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to generate backup report");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    if (primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
    reload();
    loadReport();
  }, [user, loading, primaryRole]);

  /** Driver loop: keeps calling the next step until done. Never times out — each
   *  call is short, the client just keeps calling. Survives tab switches because
   *  state is persisted server-side; resume by clicking "Resume" on a running row. */
  const drive = async (runId: string) => {
    setActiveRunId(runId);
    setPhase("starting");
    setMessage("");
    stopRef.current = false;
    let consecutiveErrors = 0;
    while (!stopRef.current) {
      try {
        const r = await stepRun({ data: { runId } });
        setPhase(r.phase);
        setMessage(r.message);
        setTotals(r.totals);
        consecutiveErrors = 0;
        if (r.done) {
          toast.success("Backup complete");
          break;
        }
        // tiny delay to let UI breathe
        await new Promise((res) => setTimeout(res, 50));
      } catch (e: any) {
        consecutiveErrors++;
        const msg = e?.message ?? String(e);
        setMessage(`error: ${msg}`);
        if (consecutiveErrors >= 5) {
          toast.error(`Backup paused after 5 errors: ${msg}. Click "Resume" to retry.`);
          break;
        }
        // Back off and retry — transient network/S3 hiccups should not break the run.
        await new Promise((res) => setTimeout(res, 2000 * consecutiveErrors));
      }
    }
    setActiveRunId(null);
    await reload();
    await loadReport();
  };

  const runNow = async () => {
    if (activeRunId) return;
    if (!confirm("Start a full database + storage + auth export to AWS S3?\n\nThe export runs in many small chunks so it cannot time out. You can leave this tab open or come back later — progress is saved server-side and you can resume an in-progress run.")) return;
    try {
      const { runId } = await startRun();
      toast.success("Backup started");
      drive(runId);
      reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to start backup");
    }
  };

  const resumeRun = (runId: string) => {
    if (activeRunId) return;
    drive(runId);
  };

  const stopDriver = () => {
    stopRef.current = true;
    toast("Stopped driving. The run stays on disk and can be resumed.");
  };

  if (loading || !user || primaryRole !== "admin") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const driving = activeRunId !== null;

  return (
    <>
      <StickyPageHeader
        icon={<CloudUpload className="h-4 w-4 text-primary" />}
        title="AWS S3 live mirror"
        subtitle="One folder on S3, kept as an exact copy of Lovable. Every run overwrites in place and deletes anything that's no longer in Lovable."
      />
      <PageBody className="space-y-6">
        <div className="border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Live mirror to AWS S3</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Mirrors every public table (chunked CSVs), the auth user list, and every Storage file to
              <code className="px-1 py-0.5 bg-muted ml-1">s3://&lt;your-bucket&gt;/heallexa-backups/current/</code>.
              Each run uploads what's changed, then a final <strong>reconcile</strong> step deletes any S3 object
              that no longer exists in Lovable — so the mirror is always an exact copy. Storage files unchanged since the last run are kept in place.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reload} disabled={loadingRuns}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loadingRuns ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <PurgeOldSnapshotsButton disabled={driving} />
            {driving ? (
              <Button onClick={stopDriver} size="sm" variant="outline">
                <StopCircle className="h-3.5 w-3.5 mr-1.5" /> Stop driving
              </Button>
            ) : (
              <Button onClick={runNow} size="sm">
                <CloudUpload className="h-3.5 w-3.5 mr-1.5" /> Export now
              </Button>
            )}
          </div>
        </div>


        {driving && (
          <div className="border border-primary/40 bg-primary/5 p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Backup in progress — phase: <span className="font-mono">{phase}</span>
            </div>
            {message && <p className="text-xs text-muted-foreground font-mono truncate">{message}</p>}
            {totals && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="Tables" value={`${totals.tablesDone}/${totals.tablesTotal}`} />
                <Stat label="Table rows" value={totals.tableRows.toLocaleString()} />
                <Stat label="Auth users" value={totals.authRows.toLocaleString()} />
                <Stat label="Storage copied" value={`${totals.storageFilesCopied.toLocaleString()} new · ${totals.storageFilesReused.toLocaleString()} reused`} />
                {totals.storageQueueRemaining > 0 && (
                  <Stat label="Files remaining" value={totals.storageQueueRemaining.toLocaleString()} />
                )}
                {(totals.staleDeleted > 0 || totals.staleRemaining > 0) && (
                  <Stat label="Stale deleted" value={`${totals.staleDeleted.toLocaleString()}${totals.staleRemaining > 0 ? ` · ${totals.staleRemaining.toLocaleString()} left` : ""}`} />
                )}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Keep this tab open for fastest progress. If you close it, click <strong>Resume</strong> on the running row below to continue. The cron job also advances any unfinished run automatically.
            </p>
          </div>
        )}

        <BackupReportPanel
          report={report}
          loading={loadingReport}
          onRefresh={loadReport}
        />

        <div>
          <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold mb-3">Recent runs</h2>
          {loadingRuns ? (
            <div className="border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              No backups yet. Click <strong>Export now</strong> to create the first one.
            </div>
          ) : (
            <div className="border border-border bg-card divide-y divide-border">
              {runs.map((r) => (
                <RunRowView
                  key={r.id}
                  r={r}
                  canResume={r.status === "running" && !driving}
                  onResume={() => resumeRun(r.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-[11px] text-muted-foreground border border-dashed border-border p-4 leading-relaxed">
          <strong>One folder, always in sync.</strong> The mirror lives at <code className="px-1 py-0.5 bg-muted">heallexa-backups/current/</code>.
          Use <strong>Purge old snapshots</strong> above to delete pre-mirror timestamped folders that were left over from the old export model.
        </div>
      </PageBody>
    </>
  );
}

function PurgeOldSnapshotsButton({ disabled }: { disabled?: boolean }) {
  const purgeFn = useServerFn(purgeOldSnapshotsFn);
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!confirm("Delete every pre-mirror snapshot folder under heallexa-backups/?\n\nOnly the live mirror at heallexa-backups/current/ will be kept. This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await purgeFn();
      toast.success(`Purge complete — ${r.deleted} deleted, ${r.skipped} skipped (${r.listed} stale objects found)`);
    } catch (e: any) {
      toast.error(e?.message ?? "Purge failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled || busy}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <StopCircle className="h-3.5 w-3.5 mr-1.5" />}
      Purge old snapshots
    </Button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border bg-background px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function fmtBytes(n: number): string {
  if (!n) return "0 B";
  const units = ["B","KB","MB","GB","TB"]; let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function RunRowView({ r, canResume, onResume }: { r: RunRow; canResume: boolean; onResume: () => void }) {
  const started = new Date(r.started_at);
  const dur = r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—";
  const statusIcon =
    r.status === "success" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> :
    r.status === "failed"  ? <AlertCircle className="h-4 w-4 text-destructive" /> :
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  return (
    <div className="p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-sm flex items-center gap-2">
          {statusIcon}
          {started.toLocaleString()}
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-border text-muted-foreground">{r.triggered_by}</span>
          {r.current_phase && r.status === "running" && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-primary/40 text-primary">phase: {r.current_phase}</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-1 truncate">
          {r.total_tables} tables · {r.total_rows.toLocaleString()} rows · {fmtBytes(r.total_bytes)} CSV · {dur}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
          Storage: {r.files_copied?.toLocaleString() ?? 0} copied · {r.files_reused?.toLocaleString() ?? 0} reused · {fmtBytes(r.storage_bytes ?? 0)}
          {" · "}Auth users: {r.auth_users_count?.toLocaleString() ?? 0}
        </p>
        {r.s3_prefix && (
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            s3://{r.s3_bucket}/{r.s3_prefix}/
          </p>
        )}
        {r.error_message && (
          <p className="text-[11px] text-destructive mt-1">{r.error_message}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canResume && (
          <Button size="sm" variant="outline" onClick={onResume}>
            <CloudUpload className="h-3.5 w-3.5 mr-1.5" /> Resume
          </Button>
        )}
        <span className={`text-xs px-2 py-0.5 border ${
          r.status === "success" ? "border-emerald-600/50 text-emerald-700" :
          r.status === "failed"  ? "border-destructive text-destructive" :
          "border-border text-muted-foreground"
        }`}>{r.status}</span>
      </div>
    </div>
  );
}

function BackupReportPanel({
  report,
  loading,
  onRefresh,
}: {
  report: BackupReport | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border bg-card">
      <div className="p-5 flex flex-wrap items-center justify-between gap-3 border-b border-border">
        <div className="min-w-0">
          <p className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            Backup status report
            {report && (
              report.ok
                ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-600/50 text-emerald-700">manifest ok</span>
                : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-destructive text-destructive">manifest drift</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Tables included in the next AWS export, with approximate row counts.
            Automatically compares the live database schema against the backup manifest.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExpanded((v) => !v)} disabled={!report}>
            {expanded ? "Hide tables" : "Show tables"}
          </Button>
        </div>
      </div>

      {loading && !report ? (
        <div className="p-6 text-sm text-muted-foreground text-center">Generating report…</div>
      ) : !report ? (
        <div className="p-6 text-sm text-muted-foreground text-center">No report yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-border">
            <ReportStat label="Manifest tables" value={report.manifestTables.length.toLocaleString()} />
            <ReportStat label="Live DB tables" value={report.dbTables.length.toLocaleString()} />
            <ReportStat label="Approx. total rows" value={report.totalRows.toLocaleString()} />
            <ReportStat
              label="Drift"
              value={
                report.missingFromManifest.length + report.staleInManifest.length === 0
                  ? "none"
                  : `${report.missingFromManifest.length} missing · ${report.staleInManifest.length} stale`
              }
              tone={report.ok ? "ok" : "warn"}
            />
          </div>

          {(report.missingFromManifest.length > 0 || report.staleInManifest.length > 0) && (
            <DriftPanel
              missing={report.missingFromManifest}
              stale={report.staleInManifest}
            />
          )}

          {expanded && (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Table</th>
                    <th className="text-right px-4 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Approx. rows</th>
                    <th className="text-right px-4 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">In manifest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.rows.map((r) => (
                    <tr key={r.name} className={r.inManifest ? "" : "bg-destructive/5"}>
                      <td className="px-4 py-1.5 font-mono">{r.name}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums">{r.rows.toLocaleString()}</td>
                      <td className="px-4 py-1.5 text-right">
                        {r.inManifest
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 inline" />
                          : <AlertCircle className="h-3.5 w-3.5 text-destructive inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground px-5 py-3 border-t border-border">
            Report generated {new Date(report.generatedAt).toLocaleString()} · Row counts are Postgres estimates (fast; may be slightly off for recently-changed tables).
          </p>
        </>
      )}
    </div>
  );
}

function ReportStat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="px-4 py-3 border-r border-border last:border-r-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${tone === "warn" ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}

function DriftPanel({ missing, stale }: { missing: string[]; stale: string[] }) {
  const [open, setOpen] = useState<null | "missing" | "stale">(null);
  const active = open === "missing" ? missing : open === "stale" ? stale : [];
  const isMissing = open === "missing";

  return (
    <>
      <div className="p-4 border-b border-border bg-muted/30 flex flex-wrap items-center gap-2">
        {missing.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
            onClick={() => setOpen("missing")}
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
            {missing.length} missing from manifest
            <PanelRightOpen className="h-3.5 w-3.5 ml-1.5 opacity-60" />
          </Button>
        )}
        {stale.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="border-amber-500/50 text-amber-700 hover:bg-amber-500/5 hover:text-amber-700"
            onClick={() => setOpen("stale")}
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
            {stale.length} stale in manifest
            <PanelRightOpen className="h-3.5 w-3.5 ml-1.5 opacity-60" />
          </Button>
        )}
        <p className="text-[11px] text-muted-foreground ml-1">
          Click to view the full list in a side panel.
        </p>
      </div>

      <Sheet open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <AlertCircle
                className={`h-4 w-4 ${isMissing ? "text-destructive" : "text-amber-600"}`}
              />
              {isMissing ? "Missing from backup manifest" : "Stale manifest entries"}
              <span className="text-xs font-normal text-muted-foreground ml-auto">
                {active.length} {active.length === 1 ? "table" : "tables"}
              </span>
            </SheetTitle>
            <SheetDescription className="text-xs">
              {isMissing
                ? "These tables exist in the database but are NOT in BACKUP_TABLES. The pre-flight check will block new backups until they're added."
                : "Listed in BACKUP_TABLES but no longer exist in the database. Safe to remove from the manifest."}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 -mx-1 flex-1 overflow-y-auto pr-1">
            <ul className="divide-y divide-border border border-border rounded-md">
              {active.map((name) => (
                <li
                  key={name}
                  className="px-3 py-2 font-mono text-xs flex items-center justify-between hover:bg-muted/40"
                >
                  <span className="truncate">{name}</span>
                  <button
                    type="button"
                    className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      navigator.clipboard?.writeText(name);
                      toast.success(`Copied "${name}"`);
                    }}
                  >
                    copy
                  </button>
                </li>
              ))}
              {active.length === 0 && (
                <li className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Nothing to show.
                </li>
              )}
            </ul>
          </div>

          <div className="pt-3 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                navigator.clipboard?.writeText(active.join("\n"));
                toast.success(`Copied ${active.length} table names`);
              }}
              disabled={active.length === 0}
            >
              Copy all as list
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
